import React, { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import CategoryPathHeader from '@/components/molecules/categoryPathHeader'
import { Box, Flex } from '@/components/atoms/Grid'
import { makeSlugPathFromCategories, SlugPath } from 'types/slugPath'
import ProductDetailSideMenu from '@/components/organisms/productDetailSideMenu'
import {
  CustomType,
  getMhubListPrefix,
  HorizontalTitledProductsSliderFetcher
} from '@/components/organisms/productsSlider/horizontalTitledProductsSlider'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import {
  ShowProductModal,
  withProductDetailPopup
} from '@/HOC/withProductDetailPopup'
import {
  AdditionalInfoPageType,
  FilterInput,
  FilterType,
  HomeBanner,
  MultiDiscount,
  Product,
  ProductSize,
  Sdk
} from '@/network/graphql.g'
import { ID } from 'graphql-ws'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { isInteger } from '@/utils/typeHelper'
import { isAvailable as isProductAvailable } from '@/network/models/product'
import { useAdditionalInfoProvider } from '@/providers/additionalInfoProvider'
import { useMhub } from '@/providers/mhubProvider'
import {
  convertAPIProductToProduct,
  convertAPIProductToProductRecommender
} from '@/types/bridgeProduct'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import {
  Desktop,
  DesktopOrTablet,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import { GetServerSidePropsContext } from 'next'
import { CacheType } from '@/utils/cache/pageDataCache'
import { getTopData } from '@/utils/topDataHelper'
import CategoryProductBanner from '@/components/organisms/categoryProductBanner'
import dynamic from 'next/dynamic'
import ImagesBrowser from '@/components/organisms/imagesBrowser'
import styled from 'styled-components'
import { useIsHydrated } from 'hooks/useIsHydrated'
import { validFor } from '@/theme/validFor'
import useDevice from '../../hooks/useDevice'
import { useScrollCondition } from '../../hooks/useScroll'
import RattingInfoSummary from '@/components/organisms/productDetailRating/rattingInfoSummary'
import { useDomain } from '@/providers/storeProvider'
import { SeoBreadcrumbList, SeoProduct } from '@/utils/seoData'
import { Routes } from '@/core/routes'

import BreadcrumbDetailMobile from '@/components/molecules/breadcrumbDetailMobile'
import { useAsset } from '@/providers/assetsProvider'
import ProductDetailRating from '@/components/organisms/productDetailRating/productDetailRating'
import {
  NavigationSource,
  useNavigationSource
} from '@/providers/navigationSourceProvider'

type ProductDetailProps = {
  product: Product
  unavailableProduct: Product
  multiDiscount: MultiDiscount
  slugPath: SlugPath
  showProductModal?: ShowProductModal
  categoryProductBanners: HomeBanner[]
  variant: number
  unavailableProductSlugPath: SlugPath
  hasSizeInUrl: boolean
  serverSelectedSize?: ProductSize | null
}

const ProductDetailSliders = dynamic(() =>
  import(
    '@/components/organisms/productDetailSliders/productDetailSliders'
  ).then((module) => module.ProductDetailSliders)
)

const ProductDetailNotAvailableTitle = dynamic(() =>
  import(
    '@/components/organisms/productDetailSideMenu/productDetailNotAvailableSideMenu'
  ).then((module) => module.ProductDetailNotAvailableTitle)
)

const ProductDetailNotAvailableSideMenu = dynamic(
  () =>
    import(
      '@/components/organisms/productDetailSideMenu/productDetailNotAvailableSideMenu'
    )
)

const ProductDetailDescription = dynamic(
  () => import('@/components/organisms/productDetailDescription'),
  { ssr: false }
)

const ProductDetail: FC<ProductDetailProps> = ({
  product,
  unavailableProduct,
  multiDiscount,
  slugPath,
  showProductModal,
  categoryProductBanners,
  variant,
  unavailableProductSlugPath,
  hasSizeInUrl,
  serverSelectedSize
}) => {
  const { request } = useGraphQl()
  const [sliderDataLoaded, setSliderDataLoaded] = useState<boolean>(false)
  const { setParameters } = useAdditionalInfoProvider()
  const { isDesktop, isTablet, isMobile } = useDevice()
  // The server does not know the screen, the mobile layout switches after hydration
  const isHydrated = useIsHydrated()
  const isMobileLayout = isHydrated && isMobile
  const [isScrolledAndStay, setIsScrolledAndStay] = useState<boolean>(false)
  const screenOffset = isDesktop || isTablet ? 200 : 600
  const isScrolledPastThreshold = useScrollCondition(
    (scroll) => scroll > screenOffset
  )

  const { subdomainName, domainName } = useDomain()
  const baseUrl = 'https://www.' + domainName + '.' + subdomainName

  const manufacturerId = product?.productBrandId
    ? product?.productBrandId
    : unavailableProduct?.productBrandId

  const algorithm = {
    similar: 'fc_similar..sort',
    category: 'fc_category..sort',
    lastSeen: 'fc_last_seen..sortLocalStorage',
    popularOfBrands: 'fc_popular_of_brands..sort'
  }

  useEffect(() => {
    if (isScrolledPastThreshold) {
      setIsScrolledAndStay(true)
    }
  }, [isScrolledPastThreshold])

  useEffect(() => {
    const revealIfThresholdUnreachable = () => {
      const scrollableDistance =
        document.documentElement.scrollHeight - window.innerHeight
      if (scrollableDistance <= screenOffset) {
        setIsScrolledAndStay(true)
      }
    }
    if (document.readyState === 'complete') {
      revealIfThresholdUnreachable()
    } else {
      window.addEventListener('load', revealIfThresholdUnreachable, {
        once: true
      })
    }
    window.addEventListener('resize', revealIfThresholdUnreachable)

    return () => {
      window.removeEventListener('load', revealIfThresholdUnreachable)
      window.removeEventListener('resize', revealIfThresholdUnreachable)
    }
  }, [screenOffset])

  const ratingScrollRef = useRef(null)

  const lastSeenKey = 'lastSeenProducts'

  const setLastSeenProducts = (productid?: string): void => {
    if (!productid) {
      // eslint-disable-next-line no-console
      console.error('function setLastSeenProducts - Product id is undefined')
      return
    }

    let idList: string[] = []

    idList = JSON.parse(localStorage.getItem(lastSeenKey) || '[]')

    if (idList.includes(productid)) {
      return
    }

    idList.unshift(productid)

    if (idList.length > 20) {
      idList.pop()
    }

    localStorage.setItem(lastSeenKey, JSON.stringify(idList))
  }

  useEffect(() => {
    setParameters(AdditionalInfoPageType.ProductDetail, product?.id)
    setLastSeenProducts(product?.id)
    return () => {
      setParameters(null, null)
    }
  }, [product?.id])

  const slidersDataFetcherFC = async (
    productId: ID,
    ids: string[]
  ): Promise<HorizontalTitledProductsSliderFetcher> => {
    const index = ids.indexOf(productId)
    if (index !== -1) {
      ids.splice(index, 1)
    }

    const filterIDs: string[] = product?.genderIds
      ? product?.genderIds.map(String)
      : unavailableProduct?.genderIds.map(String)

    const filterInput: FilterInput[] = [
      { filterType: FilterType.Gender, filterIDs: filterIDs }
    ]

    const result = await request((sdk: Sdk) => ({
      method: sdk.getProductDetailSlidersDataFC,
      variables: {
        productId,
        productIdNumber: Number(productId),
        isLoggedIn: false,
        ids,
        manufacturerId: manufacturerId,
        limit: 40,
        filter: filterInput
      },
      requestHeaders: { 'x-cachable': '1' }
    }))

    setSliderDataLoaded(true)
    const pageCode = 'product_detail'

    return {
      list: {
        similarList: getMhubListPrefix(
          pageCode,
          CustomType.similarFC,
          algorithm.similar
        ),

        popularOfBrands: getMhubListPrefix(
          pageCode,
          CustomType.popularOfBrands,
          algorithm.popularOfBrands
        ),

        categoryList: getMhubListPrefix(
          pageCode,
          CustomType.categoryFC,
          algorithm.category
        ),

        lastSeenList: getMhubListPrefix(
          pageCode,
          CustomType.lastSeenFC,
          algorithm.lastSeen
        )
      },
      products: {
        similarFCProducts:
          result?.response?.similarProducts?.products?.map((item) =>
            convertAPIProductToProduct(item)
          ) || [],
        categoryFCProducts: isAvailable
          ? []
          : result?.response?.categoryRecommended?.[0]?.products?.map((item) =>
              convertAPIProductToProduct(item)
            ) || [],
        popularOfBrandsProducts:
          result?.response?.manufacturerProducts?.products?.map((item) =>
            convertAPIProductToProduct(item)
          ) || [],
        lastSeenFCProducts:
          result?.response?.loggedOutWishlist?.map((item) =>
            convertAPIProductToProductRecommender(item)
          ) || []
      },
      algorithm: {
        similarAlgorithm: algorithm.similar,
        categoryAlgorithm: algorithm.category,
        lastSeenAlgorithm: algorithm.lastSeen,
        popularOfBrands: algorithm.popularOfBrands
      }
    }
  }

  const isAvailable = isProductAvailable(product)
  const description = product?.description?.description
  const productDetailDescriptionProps = {
    productId: product?.id,
    description,
    showDescription: product?.description.showDescription
  }

  const { pushProductDetailPage } = useMhub()
  useEffect(() => {
    pushProductDetailPage(product)
  }, [product])

  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    useState<boolean>(true)

  const [isRatingExpanded, setIsRatingExpanded] = useState<boolean>(false)

  const handleDescriptionChange = () => {
    setIsDescriptionExpanded((isExpanded) => !isExpanded)
  }

  const handleRatingChange = () => {
    setIsRatingExpanded((isExpanded) => !isExpanded)
  }

  useSpeedCurve({
    label: 'Detail'
  })

  const scrollToProductDetailRating = () => {
    const headerOffset = 52
    const elementPosition = ratingScrollRef.current.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - headerOffset
    setTimeout(() => {
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }, 0)

    setIsRatingExpanded(true)
  }

  const [hoverColorVariant, setHoverColorVariant] = useState<string>(null)
  const [hoverColorId, setHoverColorId] = useState<string>(null)

  const slidersProps = {
    product,
    unavailableProduct,
    showProductModal,
    variant,
    sliderDataLoaded,
    slidersDataFetcher: slidersDataFetcherFC
  }

  const makePath = (slugPath: SlugPath) =>
    Object.values(slugPath)
      .filter((item) => item)
      .reverse()

  const getImageUrls = (product: Product, isAvailable: boolean) => [
    product?.mainImage?.url,
    ...(isAvailable ? product.allImages.map((image) => image.url) : [])
  ]

  const imageOpacity = () => {
    if (isAvailable) {
      return 1
    } else if (!isAvailable && !hoverColorId) {
      return 0.5
    } else if (!isAvailable && unavailableProduct?.id !== hoverColorId) {
      return 1
    }
    return 0.5
  }

  const imagesBrowserProps = {
    opacity: imageOpacity(),
    imageUrls: product
      ? getImageUrls(product, isAvailable)
      : getImageUrls(unavailableProduct, isAvailable),
    badges: isAvailable ? product?.badges : [],
    labels: isAvailable ? product?.labels : [],
    showBullets: false,
    hoverColorVariant:
      product?.mainImage?.url !== hoverColorVariant ? hoverColorVariant : null
  }

  const { showReviews } = useAsset()

  const { navigationSource } = useNavigationSource()

  const makeCampaignPath = (source: NavigationSource) => [
    {
      id: source?.slug,
      title: source?.name ? source?.name : source?.from,
      url: source?.slug
        ? `/${source?.from}/${source?.slug}`
        : `/${source?.from}`
    }
  ]

  const pathsNoCategory = navigationSource
    ? navigationSource?.from !== 'category'
      ? makeCampaignPath(navigationSource)
      : null
    : null

  const paths =
    isAvailable && slugPath
      ? pathsNoCategory
        ? pathsNoCategory
        : makePath(slugPath)
      : makePath(unavailableProductSlugPath)

  return (
    <>
      <MobileOnly>
        <BreadcrumbDetailMobile paths={paths} productId={product?.id} />
      </MobileOnly>
      <PageContentWrapper mb={50}>
        <SeoProduct baseUrl={baseUrl} product={product}></SeoProduct>
        <SeoBreadcrumbList baseUrl={baseUrl} slugPathValues={paths} />
        <MediaContextProvider>
          <DesktopOrTablet>
            <CategoryProductBanner banners={categoryProductBanners} />
          </DesktopOrTablet>
        </MediaContextProvider>
        <DesktopOrTabletOnly>
          <CategoryPathHeader
            mx={{ desktop: 0, tablet: 0, mobile: -10 }}
            paths={paths}
          />
        </DesktopOrTabletOnly>
        <Flex
          mt={{ desktop: 10, tablet: 10, mobile: 0 }}
          mb={20}
          justifyContent="space-between"
          maxWidth="100vw"
          flexDirection="column"
          data-testid="productDetailWrapper"
        >
          <Flex
            flexDirection={{ desktop: 'row', tablet: 'row', mobile: 'column' }}
          >
            <Flex
              flexDirection="column"
              mr={{ desktop: 24, tablet: 24, mobile: 0 }}
              flex="1 1 auto"
              maxWidth="100%"
              minWidth="30%"
            >
              <Box position="relative">
                {!isAvailable && (
                  <ProductDetailNotAvailableTitle isModal={false} />
                )}
                <MediaContextProvider>
                  <Desktop>
                    <ImagesBrowser
                      {...imagesBrowserProps}
                      modalMode={!isAvailable}
                      modalImageArea="pdp-unavailable"
                      product={product}
                    />
                  </Desktop>
                  <TabletOrMobile>
                    <ImagesBrowser {...imagesBrowserProps} product={product} />
                  </TabletOrMobile>
                </MediaContextProvider>
              </Box>
              {isAvailable && !isMobileLayout && (
                <StyledBox pr={34} mt={60}>
                  <ProductDetailDescription
                    {...productDetailDescriptionProps}
                    isExpanded={isDescriptionExpanded}
                    handleChange={handleDescriptionChange}
                    product={product}
                  />
                  {product?.productRateData?.canShow && showReviews ? (
                    <Box ref={ratingScrollRef}>
                      <ProductDetailRating
                        isExpanded={isRatingExpanded}
                        handleChange={handleRatingChange}
                        product={product}
                        productRateData={product?.productRateData}
                      />
                    </Box>
                  ) : null}
                  {!showReviews ? (
                    <RattingInfoSummary product={product} />
                  ) : null}
                </StyledBox>
              )}
            </Flex>
            <Box
              flex={{
                desktop: `0 0 33%`,
                tablet: `0 0 50%`,
                mobile: `0 0 100%`
              }}
              width={{ desktop: '33%', tablet: '50%', mobile: '100%' }}
            >
              {isAvailable ? (
                <ProductDetailSideMenu
                  product={product}
                  setHoverColorVariant={setHoverColorVariant}
                  scrollToProductDetailRating={scrollToProductDetailRating}
                  multiDiscount={multiDiscount}
                  hasSizeInUrl={hasSizeInUrl}
                  serverSelectedSize={serverSelectedSize}
                />
              ) : (
                <ProductDetailNotAvailableSideMenu
                  isExpanded={isDescriptionExpanded}
                  handleChange={handleDescriptionChange}
                  unavailableProduct={unavailableProduct}
                  setHoverColorVariant={setHoverColorVariant}
                  setHoverColorId={setHoverColorId}
                />
              )}
            </Box>
            {isAvailable && isMobileLayout && (
              <StyledBox>
                <ProductDetailDescription
                  {...productDetailDescriptionProps}
                  isExpanded={isDescriptionExpanded}
                  handleChange={handleDescriptionChange}
                  product={product}
                />
                {product?.productRateData?.canShow && showReviews ? (
                  <Box ref={ratingScrollRef}>
                    <ProductDetailRating
                      isExpanded={isRatingExpanded}
                      handleChange={handleRatingChange}
                      product={product}
                      productRateData={product?.productRateData}
                    />
                  </Box>
                ) : null}
                {!showReviews ? <RattingInfoSummary product={product} /> : null}
              </StyledBox>
            )}
          </Flex>
          {isScrolledAndStay ? (
            <ProductDetailSliders {...slidersProps} />
          ) : null}
        </Flex>
      </PageContentWrapper>
    </>
  )
}

const getCommonProps = (
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  context: GetServerSidePropsContext
): { variant: number } => ({
  variant: Math.floor(Math.random() * 5)
})

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withTopNavigation,
    withStore
  }): Promise<GetServerSideProps<ProductDetailProps>> => {
    const [productId] = context.params.slug as string[]
    const sizeParamRaw = context.query.size
    const sizeParam = Array.isArray(sizeParamRaw)
      ? sizeParamRaw[0]
      : (sizeParamRaw as string | undefined)

    if (!isInteger(productId)) {
      return {
        notFound: true
      }
    }

    const { success, response, error } = await requestThrow401({
      method: sdk.getProductDetail,
      variables: {
        productId,
        withTopNavigation,
        withStore
      },
      mainResponse: (response) => response?.product
    })

    if (error?.status == 500) {
      return {
        redirect: {
          destination: Routes.internalServerError,
          permanent: false
        }
      }
    }

    const isProductNotAvailable = response?.product === null

    const { response: unavailableProductResponse } = await requestThrow401({
      method: isProductNotAvailable ? sdk.getUnavailableProductDetail : null,
      variables: {
        productId,
        withTopNavigation,
        withStore
      },
      mainResponse: (response) => response?.unavailableProduct
    })

    if (
      !success ||
      (isProductNotAvailable &&
        unavailableProductResponse?.unavailableProduct === null)
    ) {
      return {
        notFound: true
      }
    }
    const serverSelectedSize: ProductSize | null = sizeParam
      ? response?.product?.sizes?.find((s) => s.id === sizeParam) ?? null
      : null
    const hasSizeInUrl = Boolean(serverSelectedSize)

    const slugPath: SlugPath = makeSlugPathFromCategories(
      response?.product?.mainCategory?.traversablePath ?? []
    )

    const unavailableProductSlugPath: SlugPath = makeSlugPathFromCategories(
      unavailableProductResponse?.unavailableProduct?.mainCategory
        ?.traversablePath ?? []
    )

    const multiDiscountPromise = response?.product
      ? await requestThrow401({
          method: sdk.getMultiDiscount,
          variables: { productId },
          mainResponse: (response) => response?.multiDiscount
        })
      : Promise.resolve({ success: false, response: null })

    const multiDiscountData = await multiDiscountPromise

    const multiDiscount = multiDiscountData.success
      ? multiDiscountData.response?.multiDiscount
      : null

    return {
      props: {
        ...{
          topData: getTopData(response),
          disableDaktela: true,
          seoTags: response?.seoTags,
          key: `product/${productId}`,
          product: response?.product as Product,
          unavailableProduct:
            (unavailableProductResponse?.unavailableProduct as Product) ?? null,
          slugPath,
          unavailableProductSlugPath,
          multiDiscount,
          hasSizeInUrl,
          serverSelectedSize: serverSelectedSize ?? null,
          categoryProductBanners:
            response?.homeBannersForHomeBannerCategory ?? null
        },
        ...getCommonProps(context)
      }
    }
  },
  { cacheType: CacheType.ProductDetail, getCommonProps }
)

export default withProductDetailPopup(ProductDetail)

// Both breadcrumbs render on the server and hide by CSS, so the HTML matches
// the client on every screen
const MobileOnly = styled.div`
  ${validFor.tablet`display: none;`}
  ${validFor.desktop`display: none;`}
`

const DesktopOrTabletOnly = styled.div`
  ${validFor.mobile`display: none;`}
`

const StyledBox = styled(Box)`
  flex-direction: column;
`
